import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { callsService } from '../../../utils/callsService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubmissionHistory = ({ onResubmit }) => {
  const { user, isAuthenticated } = useAuth();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (isAuthenticated && user) {
      loadCalls();
    }
  }, [isAuthenticated, user, filter]);

  const loadCalls = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await callsService?.getUserCalls(user?.id, 'publisher');
      
      let filteredCalls = data || [];
      if (filter !== 'all') {
        filteredCalls = filteredCalls?.filter(call => call?.status === filter);
      }
      
      setCalls(filteredCalls);
    } catch (err) {
      setError(err?.message || 'Failed to load submission history');
    } finally {
      setLoading(false);
    }
  };

  const handleResubmit = (call) => {
    const resubmissionData = {
      insurance_type: call?.insurance_type,
      call_quality: call?.call_quality,
      customer_age: call?.customer_age,
      customer_zip_code: call?.customer_zip_code,
      estimated_premium: call?.estimated_premium,
      reserve_price: call?.reserve_price,
      call_duration: call?.call_duration,
      call_notes: call?.call_notes,
      demographic_data: call?.demographic_data || {}
    };
    
    onResubmit?.(resubmissionData);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sold': return { icon: 'CheckCircle', className: 'text-success' };
      case 'in_auction': return { icon: 'Clock', className: 'text-warning' };
      case 'pending': return { icon: 'Circle', className: 'text-muted-foreground' };
      case 'expired': return { icon: 'XCircle', className: 'text-destructive' };
      case 'cancelled': return { icon: 'Ban', className: 'text-destructive' };
      default: return { icon: 'Circle', className: 'text-muted-foreground' };
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'in_auction': return 'In Auction';
      case 'sold': return 'Sold';
      case 'expired': return 'Expired';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="text-center py-8">
          <Icon name="History" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Sign In Required</h3>
          <p className="text-muted-foreground">Please sign in to view your submission history</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="History" size={20} className="text-accent" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Submission History</h2>
              <p className="text-sm text-muted-foreground">Track your recent call submissions</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e?.target?.value)}
              className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_auction">In Auction</option>
              <option value="sold">Sold</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>
      </div>
      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <Icon name="Loader2" size={32} className="text-muted-foreground animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading submission history...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">Failed to Load History</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button variant="outline" onClick={loadCalls}>
              Try Again
            </Button>
          </div>
        ) : calls?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Submissions Found</h3>
            <p className="text-muted-foreground">
              {filter === 'all' ? "You haven't submitted any calls yet" 
                : `No calls found with status: ${getStatusLabel(filter)}`
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {calls?.map((call) => {
              const statusConfig = getStatusIcon(call?.status);
              return (
                <div
                  key={call?.id}
                  className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2">
                          <Icon 
                            name={statusConfig?.icon} 
                            size={16} 
                            className={statusConfig?.className} 
                          />
                          <span className="font-medium text-foreground">
                            {call?.insurance_type?.charAt(0)?.toUpperCase() + call?.insurance_type?.slice(1)} Insurance
                          </span>
                          <span className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground">
                            {call?.call_quality}
                          </span>
                        </div>
                        <span className={`text-sm font-medium ${statusConfig?.className}`}>
                          {getStatusLabel(call?.status)}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">ZIP:</span>
                          <span className="ml-1 text-foreground">{call?.customer_zip_code}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Premium:</span>
                          <span className="ml-1 text-foreground">${call?.estimated_premium}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Reserve:</span>
                          <span className="ml-1 text-foreground">${call?.reserve_price}</span>
                        </div>
                        {call?.final_price && (
                          <div>
                            <span className="text-muted-foreground">Sold:</span>
                            <span className="ml-1 text-success font-medium">${call?.final_price}</span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                        <span>
                          Submitted: {new Date(call?.created_at)?.toLocaleDateString()}
                        </span>
                        {call?.bids?.length > 0 && (
                          <span>{call?.bids?.length} bid{call?.bids?.length > 1 ? 's' : ''}</span>
                        )}
                      </div>

                      {call?.call_notes && (
                        <p className="text-sm text-muted-foreground italic">
                          "{call?.call_notes}"
                        </p>
                      )}
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      {(call?.status === 'expired' || call?.status === 'cancelled') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleResubmit(call)}
                        >
                          Resubmit
                        </Button>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="ExternalLink"
                      >
                        View
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmissionHistory;